import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useScroll } from 'framer-motion';
import { skillCategories } from '../../Data/skills';
import { useLang } from '../../context/LanguageContext';
import { t } from '../../translations';

// Adaptation of the inspiration file for the portfolio project

const Skills = () => {
    const sectionRef = useRef(null);
    const containerRef = useRef(null);

    // Use framer-motion's useScroll instead of custom hook
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"]
    });

    // Transform scroll progress to a value usable by our animation logic (0 to 1)
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        return scrollYProgress.on("change", (latest) => {
            setProgress(latest);
        });
    }, [scrollYProgress]);

    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [particles, setParticles] = useState([]);
    const particleIdRef = useRef(0);

    // Particle creation function
    const createParticle = useCallback((x, y, color) => {
        particleIdRef.current += 1;
        return {
            id: particleIdRef.current,
            x,
            y,
            vx: (Math.random() - 0.5) * 4,
            vy: (Math.random() - 0.5) * 4,
            life: 1,
            color
        };
    }, []);

    // Optimized particle system
    useEffect(() => {
        const updateParticles = () => {
            setParticles(prev => {
                const filtered = prev
                    .map(particle => ({
                        ...particle,
                        x: particle.x + particle.vx,
                        y: particle.y + particle.vy,
                        life: particle.life - 0.03
                    }))
                    .filter(particle => particle.life > 0)
                    .slice(0, 20); // Limit count

                return filtered;
            });
        };

        const interval = setInterval(updateParticles, 32);
        return () => clearInterval(interval);
    }, []);

    // Create particles on node expansion
    useEffect(() => {
        if (containerRef.current && expandedNodes.size > 0) {
            const rect = containerRef.current.getBoundingClientRect();
            const latestExpandedNode = Array.from(expandedNodes).pop();
            const node = skillCategories.find(n => n.id === latestExpandedNode);

            if (node) {
                const x = (node.x / 100) * rect.width;
                const y = (node.y / 100) * rect.height;

                for (let i = 0; i < 3; i++) {
                    setParticles(prev => [...prev, createParticle(x, y, node.color)]);
                }
            }
        }
    }, [expandedNodes, createParticle]);

    const centerX = 50;
    const centerY = 50;

    // Calculate angle from center to node to determine "outward" direction
    const getOutwardAngle = (nodeX, nodeY) => {
        const dx = nodeX - centerX;
        const dy = nodeY - centerY;
        return Math.atan2(dy, dx);
    };

    const getSkillPosition = (index, total, nodeX, nodeY) => {
        let radius = 100;
        let arcSpan = Math.PI * 0.6; // ~110 degrees default

        // Logic for larger groups
        if (total > 7) { // Specifically for Frontend (8 items)
            radius = 200;
            arcSpan = Math.PI * 1.1; // Wider arc (~200 deg)
        } else if (total > 5) { // Backend (6), Tools (7)
            radius = 170;
            arcSpan = Math.PI * 0.9;
        } else {
            radius = 140;
        }
        const outwardAngle = getOutwardAngle(nodeX, nodeY);
        const startAngle = outwardAngle - (arcSpan / 2);
        // Be careful with divider: if total is 1, step is 0 (centered)
        const step = total > 1 ? arcSpan / (total - 1) : 0;

        const angle = total === 1 ? outwardAngle : startAngle + (index * step);

        return {
            x: Math.cos(angle) * radius,
            y: Math.sin(angle) * radius
        };
    };

    const toggleExpansion = (nodeId) => {
        setExpandedNodes(prev => {
            const newSet = new Set();
            if (prev.has(nodeId)) {
                return newSet; // Collapse if clicked again
            }
            newSet.add(nodeId); // Open just this one (auto-close others)
            return newSet;
        });
    };

    const activeNodeId = Array.from(expandedNodes)[0];

    // Logic: Active stays normal/highlighted. Others fade slightly but STAY VISIBLE.
    const getNodeVisuals = (node) => {
        if (!activeNodeId) return { scale: 1, opacity: 1, grayscale: 0 };

        if (node.id === activeNodeId) {
            return { scale: 1.1, opacity: 1, grayscale: 0 };
        }
        // Non-active nodes: dim them, reduce scale slightly
        return { scale: 0.9, opacity: 0.3, grayscale: 0.5 };
    };

    const drawWebLines = () => {
        const lines = [];
        const globalOpacity = activeNodeId ? 0.2 : 1; // Dim all lines when focused

        const strokeClass = "stroke-black/15 dark:stroke-white/15";

        // Center connections
        skillCategories.forEach((node, index) => {
            const opacity = Math.max(0.1, progress * 0.8) * globalOpacity;
            lines.push(
                <line
                    key={`center-${index}`}
                    x1={`${centerX}%`}
                    y1={`${centerY}%`}
                    x2={`${node.x}%`}
                    y2={`${node.y}%`}
                    className={`${strokeClass} transition-colors duration-500`}
                    strokeWidth="1.5"
                    style={{
                        opacity: opacity,
                        strokeDasharray: '2,1'
                    }}
                />
            );
        });

        // Outer web connections
        if (progress > 0.3) {
            skillCategories.forEach((node, index) => {
                const nextIndex = (index + 1) % skillCategories.length;
                const nextNode = skillCategories[nextIndex];
                const opacity = Math.max(0.1, progress * 0.6) * globalOpacity;
                lines.push(
                    <line
                        key={`web-${index}`}
                        x1={`${node.x}%`}
                        y1={`${node.y}%`}
                        x2={`${nextNode.x}%`}
                        y2={`${nextNode.y}%`}
                        className={`${strokeClass} transition-colors duration-500`}
                        strokeWidth="1"
                        style={{
                            opacity: opacity,
                            strokeDasharray: '3,2'
                        }}
                    />
                );
            });
        }

        return lines;
    };

    const { lang } = useLang();
    const tr = t[lang].skills;

    return (
        <section id="skills" className="py-12 sm:py-16 md:py-20 bg-bg transition-colors duration-300 overflow-hidden" ref={sectionRef}>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
            >
                <span className="text-accent-text font-semibold tracking-wider text-[13px] uppercase">Capabilities</span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-label mt-2">{tr.title}</h2>
            </motion.div>

            <div
                ref={containerRef}
                className="relative w-full h-[500px] sm:h-[600px] md:h-[700px] lg:h-[900px] mx-auto opacity-100 transition-all duration-300"
                style={{
                    background: 'transparent',
                    cursor: 'grab' // Indicate draggable
                }}
                onMouseDown={() => (containerRef.current.style.cursor = 'grabbing')}
                onMouseUp={() => (containerRef.current.style.cursor = 'grab')}
            >
                {/* Draggable Universe Container */}
                <motion.div
                    drag
                    // Allow dragging freely but with some elastic resistance if pulled too far
                    dragConstraints={{ left: -200, right: 200, top: -200, bottom: 200 }}
                    className="w-full h-full relative"
                >
                    {/* Background Web Lines */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
                        {drawWebLines()}
                    </svg>

                    {/* Center Hub Text */}
                    <div
                        className="absolute rounded-full flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                        style={{
                            left: `${centerX}%`,
                            top: `${centerY}%`,
                            width: '90px',
                            height: '90px',
                            zIndex: 3,
                            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(147, 51, 234, 0.9))',
                            boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
                            opacity: activeNodeId ? 0.6 : 1
                        }}
                    >
                        <span className="text-white font-bold text-xs text-center">SKILLS<br />HUB</span>
                    </div>

                    {/* Categories Nodes */}
                    {skillCategories.map((node) => {
                        const isExpanded = node.id === activeNodeId;
                        const { scale, opacity, grayscale } = getNodeVisuals(node);

                        return (
                            <React.Fragment key={node.id}>
                                {/* Main Node */}
                                <motion.div
                                    animate={{
                                        scale: scale,
                                        opacity: opacity,
                                        filter: `grayscale(${grayscale})`,
                                        x: "-50%",
                                        y: "-50%"
                                    }}
                                    transition={{ type: "spring", stiffness: 60, damping: 15 }}
                                    className="absolute rounded-full flex flex-col items-center justify-center cursor-pointer group"
                                    style={{
                                        left: `${node.x}%`,
                                        top: `${node.y}%`,
                                        width: '90px',
                                        height: '90px',
                                        zIndex: 10,
                                        background: isExpanded
                                            ? `linear-gradient(135deg, ${node.color}, #000)`
                                            : `linear-gradient(135deg, ${node.color}cc, ${node.color}ff)`,
                                        border: `2px solid ${node.color}`,
                                        boxShadow: `0 0 15px ${node.color}33`
                                    }}
                                    onClick={() => toggleExpansion(node.id)}
                                >
                                    <div className={`text-white text-center font-bold text-xs md:text-sm leading-tight select-none transition-opacity duration-300 drop-shadow-md`}>
                                        {node.category.split(' ').map((line, i) => <div key={i}>{line}</div>)}
                                    </div>
                                </motion.div>

                                {/* Children Nodes (Directional) */}
                                {isExpanded && node.skills.map((skill, index) => {
                                    // Pass node coords to calculate outward direction
                                    const pos = getSkillPosition(index, node.skills.length, node.x, node.y);
                                    return (
                                        <motion.div
                                            key={`${node.id}-${skill}`}
                                            initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                                            animate={{
                                                scale: 1,
                                                opacity: 1,
                                                x: pos.x,
                                                y: pos.y
                                            }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 50,
                                                delay: index * 0.05
                                            }}
                                            className="absolute z-20"
                                            style={{
                                                left: `${node.x}%`,
                                                top: `${node.y}%`
                                            }}
                                        >
                                            <div
                                                className="px-4 py-2 text-white text-sm font-bold rounded-full shadow-xl whitespace-nowrap backdrop-blur-md transition-all duration-300"
                                                style={{
                                                    background: `rgba(0,0,0,0.7)`,
                                                    border: `1px solid ${node.color}`,
                                                    boxShadow: `0 0 15px ${node.color}40`,
                                                    textShadow: `0 0 5px ${node.color}80`
                                                }}
                                            >
                                                {skill}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}

                    {/* Particles - Inside draggable area */}
                    {particles.map(particle => (
                        <div
                            key={particle.id}
                            className="absolute w-1 h-1 rounded-full pointer-events-none bg-blue-500 dark:bg-white"
                            style={{
                                left: particle.x,
                                top: particle.y,
                                opacity: particle.life,
                                backgroundColor: particle.color
                            }}
                        />
                    ))}
                </motion.div>

                {/* Instructions - Fixed Position (outside drag area but inside container) */}
                <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
                    <span className="text-xs sm:text-sm text-label-tertiary uppercase tracking-[0.2em]">
                        {tr.instruction}
                    </span>
                </div>
            </div>
        </section>
    );
};

export default Skills;
